import { withServerPosition } from "@/lib/tableOrder";
import Dexie from "dexie";
import { mapDisplaySettings } from "@/lib/settingsMap";
import { uuid } from "@/lib/uuid";

export const defaultSettings = {
  restaurantName: "Vibez POS", gst: 5, serviceCharge: 7.5, autoKOT: false, allowNegativeStock: false,
  // Realtime defaults ON: with it off, a KOT fired by a captain never reaches the
  // kitchen display and the till never learns the order changed. The Admin toggle
  // stays for isolating a terminal when the network itself is the suspect.
  receiptFooter: "Thank you for dining with us!", tablePrefix: "T", 
  kds_warn_minutes: 10, kds_alert_minutes: 15, kds_sound_enabled: true, kds_new_ticket_flash_s: 5, kds_recall_window_s: 30, realtimeEnabled: true,
};

export const defaultPrinters = [
  { id: "billing-network", name: "Billing Receipt Printer", connection: "network", role: "billing", host: "192.168.1.42", port: 9100, sections: "billing", enabled: true, status: "unverified" },
  { id: "kitchen-usb", name: "Main Kitchen KOT Printer", connection: "usb", role: "kitchen", vendorId: "0x04b8", productId: "0x0e15", devicePath: "/dev/usb/lp0", sections: "main-kitchen", enabled: true, status: "unverified" },
];

export const offlineDb = new Dexie("VibezPOSOffline");
offlineDb.version(1).stores({
  menu: "&id, category, code",
  areas: "&id",
  area_tables: "&id, zone, status",
  tables: "&id, status",
  tickets: "&id, table, order_id",
  orders: "&id, status, updated_at",
  payments: "&id, order_id, status",
  customers: "&id, phone",
  inventory: "&id, status",
  settings: "&key",
  printers: "&id, role, status",
  offline_sync_queue: "&action_id, created_at, status, entity_id, type",
  meta: "&key",
});

// v2 adds the payment_modes and order_types the bootstrap payload now carries.
// They were originally appended to version(1) instead, which is invisible on a
// fresh install and broken everywhere else: a terminal that had already opened the
// database at version 1 keeps the schema it was created with, so `offlineDb.
// payment_modes` on that terminal throws "table does not exist" and the whole
// bootstrap transaction aborts. A new store is a new version — Dexie only needs
// the stores that changed, and carries the rest of the schema forward.
offlineDb.version(2).stores({
  payment_modes: "&name",
  order_types: "&name",
});

// v3 adds the pure event log and materialized mesh aggregates for Phase M0.
// We index by aggregate_id to load an entire aggregate's history quickly.
// We also add mesh_identity to store the non-extractable WebCrypto keypair.
offlineDb.version(3).stores({
  captain_events: "&event_id, aggregate_id, type, created_at, [aggregate_id+type]",
  captain_materialized: "&aggregate_id, table_id, status, revision",
  mesh_identity: "&id"
});

// v4 makes reconciliation queryable.
//   _sync_state  — indexed so the flush can find un-acknowledged events without
//                  scanning the whole log. "local" until the SERVER says
//                  otherwise; "server_acked" only on the strength of a row in
//                  the backend's mesh_reconciled_events ledger.
//   mesh_order_map — the local aggregate_id -> authoritative server order_id.
//                  Keyed on aggregate_id, so a second reconciliation of the same
//                  offline order finds the order that already exists.
offlineDb.version(4).stores({
  captain_events: "&event_id, aggregate_id, type, created_at, _sync_state, [aggregate_id+type], [aggregate_id+_sync_state]",
  captain_materialized: "&aggregate_id, table_id, status, revision",
  mesh_identity: "&id",
  mesh_order_map: "&aggregate_id, order_id",
}).upgrade(tx => tx.table("captain_events").toCollection().modify(e => {
  if (!e._sync_state) e._sync_state = "local";
}));

export const dbStore = name => offlineDb.table(name);

export async function bootstrapOfflineDb() {
  await offlineDb.transaction("rw", [dbStore("menu"), dbStore("areas"), dbStore("area_tables"), dbStore("tables"), dbStore("tickets"), dbStore("settings"), dbStore("printers"), dbStore("meta"), dbStore("payment_modes"), dbStore("order_types")], async () => {
    // No demo menu. The seed used to bulkPut data/posData's twelve invented dishes
    // ("Paneer Tikka ₹325", "Butter Chicken ₹425") into every terminal whose
    // cacheBootstrapFromServer had not yet succeeded — and that function swallows
    // every failure with `return false`, so a till that had never reached the server
    // showed a priced, tappable menu belonging to no restaurant. A captain could
    // punch those lines and a cashier could bill them. The menu store now starts
    // empty and the screens say the menu has not been loaded from this server.
    // No demo areas, floor tiles or tables. The seed rows carried invented statuses
    // ("occupied", "billing") and guest names ("Sharma · 4 pax") but no table_id and
    // no number, so a terminal that had never completed a bootstrap painted a busy
    // floor nobody could act on — Shift/Merge answered "that table has no running
    // order" for a tile the screen showed as occupied. An empty floor with an honest
    // "not loaded from the server" message is the truth on such a terminal.
    // No demo tickets either. The kitchen display reads GET /api/kds/tickets, so a seeded
    // ticket would be an order the kitchen believes it has to cook. The store stays
    // for useKotActions' offline record of a KOT the captain fired.
    if (await offlineDb.settings.count() === 0) await offlineDb.settings.put({ key: "main", value: defaultSettings });
    if (await offlineDb.printers.count() === 0) await offlineDb.printers.bulkPut(defaultPrinters);
    await offlineDb.meta.put({ key: "schema-version", value: 1 });
    // "demo" now means only "local defaults — nothing has come from the server yet";
    // cacheBootstrapFromServer overwrites it with "server" on a successful sync. The
    // marker used to distinguish the restaurant's menu from Vibez's sample, which
    // rendered identically. There is no sample any more, but the flag is still the
    // one durable record that this terminal has never completed a bootstrap.
    await offlineDb.meta.put({ key: "bootstrap-source", value: "demo" });
  });
}

export async function cacheBootstrapFromServer(token) {
  try {
    const capabilityResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/`, { headers: token ? { Authorization: `Bearer ${token}` } : {}, cache: "no-store" });
    if (!capabilityResponse.ok) return false;
    const capabilityPayload = await capabilityResponse.json();
    const advertised = capabilityResponse.headers.get("X-Vibez-Capabilities") || "";
    if (!advertised.includes("bootstrap") && !capabilityPayload.capabilities?.includes("bootstrap")) return false;
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bootstrap`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!response.ok) return false;
    const payload = await response.json();
    await offlineDb.transaction("rw", [dbStore("menu"), dbStore("areas"), dbStore("area_tables"), dbStore("tables"), dbStore("payment_modes"), dbStore("order_types")], async () => {
      if (payload.menu?.length) { await offlineDb.menu.clear(); await offlineDb.menu.bulkPut(withServerPosition(payload.menu)); }
      if (payload.areas?.length) { await offlineDb.areas.clear(); await offlineDb.areas.bulkPut(withServerPosition(payload.areas)); }
      if (payload.area_tables?.length) { await offlineDb.area_tables.clear(); await offlineDb.area_tables.bulkPut(payload.area_tables); }
      if (payload.tables?.length) { await dbStore("tables").clear(); await dbStore("tables").bulkPut(payload.tables); }
      if (payload.payment_modes?.length) { await dbStore("payment_modes").clear(); await dbStore("payment_modes").bulkPut(payload.payment_modes); }
      if (payload.order_types?.length) { await dbStore("order_types").clear(); await dbStore("order_types").bulkPut(payload.order_types); }
    });
    
    // The mesh verification key. This is the PUBLIC half of the server's signing
    // key and nothing else — it checks a peer's authorization and cannot issue
    // one. An earlier build cached `mesh_secret`, which was JWT_SECRET itself.
    if (payload.mesh_public_key_jwk) {
      await offlineDb.settings.put({ key: "mesh_public_key_jwk", value: payload.mesh_public_key_jwk });
      // Enrolment deliberately does NOT happen here any more.
      //
      // The server keeps ONE active credential per (actor_id, device_id):
      // enroll_device supersedes the previous one and bumps credential_version.
      // Login already enrolled this actor on this device, so a second enrolment
      // here — with the separate device keypair — superseded the actor's
      // credential seconds after it was issued. Every mesh event then failed
      // `registry check failed: credential superseded`, was never acknowledged,
      // and the aggregate re-flushed every 15s forever.
      //
      // One actor+device, one credential, issued at login. The guard that used
      // to sit here could never fire either: it read the "primary" identity
      // record while enrollActorIdentity writes one keyed by employee_id.
    }

    await cacheDisplaySettings(token);
    await offlineDb.meta.put({ key: "bootstrap-source", value: "server" });
    return true;
  } catch { return false; }
}

// The backend configuration resolver is the source of truth; map it onto Vibez's
// camelCase shape. A failure here must not fail the whole bootstrap — the menu and
// tables are already written by the time this runs.
//
// This is the ONE path that mirrors server settings onto a terminal. Nothing else
// may write the settings document from the server, or two owners drift apart.
export async function cacheDisplaySettings(token) {
  // GET /api/settings/resolved is captain-or-above; the login page has no token, so
  // asking anyway spent a request to be told 401 and logged an error in the console
  // of every unauthenticated load. The menu and tables above are public and still
  // pre-cache. Settings arrive on the bootstrap that follows sign-in.
  if (!token) return;
  try {
    const terminalId = sessionStorage.getItem("vibez-terminal-id") || "";
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/resolved?terminal_id=${terminalId}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) return;
    // The resolver answers with an envelope, not a flat map:
    //   {context: {...}, settings: {key: {value, source_scope, overridden}}}
    // Storing that whole object was how every terminal ended up back on the
    // hardcoded defaults — `settings.discountMaxPct` and friends were never written,
    // so the till read 5% GST and "Cap 100%" no matter what the owner had saved.
    const resolved = await response.json();
    const flat = Object.fromEntries(
      Object.entries(resolved?.settings || {}).map(([key, entry]) => [
        key,
        entry && typeof entry === "object" && "value" in entry ? entry.value : entry,
      ])
    );
    const current = (await offlineDb.settings.get("main"))?.value || defaultSettings;
    // The server's own key names ride through untouched (KDSPage reads
    // kds_warn_minutes / kds_alert_minutes straight off the document), and
    // mapDisplaySettings adds the camelCase aliases the billing screens read.
    await putSetting(mapDisplaySettings(flat, { ...defaultSettings, ...current, ...flat }));
  } catch { /* keep whatever settings are already cached */ }
}

export async function replaceCollection(tableName, rows) {
  const table = dbStore(tableName);
  await offlineDb.transaction("rw", table, async () => {
    await table.clear();
    if (rows.length) await table.bulkPut(rows);
  });
}

export async function putSetting(value) {
  await offlineDb.settings.put({ key: "main", value });
}

export async function saveSyncMeta(terminalId) {
  await offlineDb.meta.bulkPut([
    { key: "terminal-id", value: terminalId },
    { key: "sync-url", value: process.env.REACT_APP_BACKEND_URL },
  ]);
}

export async function queueMutation(type, entityId, payload, terminalId) {
  const action = {
    action_id: uuid(), entity_id: String(entityId), type, payload, terminal_id: terminalId,
    status: "pending", attempt_count: 0, created_at: new Date().toISOString(),
  };
  await offlineDb.offline_sync_queue.add(action);
  if ("serviceWorker" in navigator) {
    try {
      const registration = await navigator.serviceWorker.ready;
      if (registration.sync) await registration.sync.register("vibez-offline-sync");
      else registration.active?.postMessage({ type: "FLUSH_QUEUE" });
    } catch {
      const registration = await navigator.serviceWorker.getRegistration();
      registration?.active?.postMessage({ type: "FLUSH_QUEUE" });
    }
  }
  return action;
}

export async function flushOfflineQueue(token) {
  const actions = await offlineDb.offline_sync_queue.where("status").equals("pending").sortBy("created_at");
  if (!actions.length) return { flushed: 0 };
  try {
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sync/actions/batch`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      credentials: "include",
      body: JSON.stringify({ terminal_id: actions[0].terminal_id, actions }),
    });
    if (!response.ok) throw new Error("Sync endpoint unavailable");
    const result = await response.json();
    const accepted = result.accepted_action_ids || [];
    const conflicts = result.conflicts || [];
    // Conflicted and rejected actions will never be accepted on a retry — the
    // server has already decided. Leaving them queued means the queue grows for
    // ever and every flush re-sends them. Drop them, but keep the reasons so a
    // silent discard is inspectable rather than invisible.
    const settled = [...accepted, ...conflicts.map(c => c.action_id).filter(Boolean)];
    await offlineDb.offline_sync_queue.bulkDelete(settled);
    if (conflicts.length) {
      const previous = (await offlineDb.meta.get("sync-conflicts"))?.value || [];
      await offlineDb.meta.put({
        key: "sync-conflicts",
        value: [...previous, ...conflicts.map(c => ({ ...c, at: new Date().toISOString() }))].slice(-100),
      });
    }
    return { flushed: accepted.length, rejected: conflicts.length };
  } catch {
    await Promise.all(actions.map(action => offlineDb.offline_sync_queue.update(action.action_id, { attempt_count: action.attempt_count + 1, last_attempt_at: new Date().toISOString() })));
    return { flushed: 0 };
  }
}